import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

/* Requêtes en vol — signal d'attente de l'export Excel. Celui-ci monte les
   onglets d'un hub hors écran puis convertit leurs tableaux : il lui faut savoir
   QUAND les données sont arrivées. Le déduire du DOM ne marche pas — chaque page
   nomme son état de chargement autrement, et une vue légitimement vide (« aucune
   charge sociale saisie ») est indiscernable d'une vue en retard. Le compteur,
   lui, ne ment pas.

   Les intercepteurs sont posés AVANT ceux de l'authentification : sur un 401,
   celui du refresh rejoue la requête, qui repasse par le compteur — chaque
   départ a bien son retour. */
let inFlight = 0;

/** Nombre de requêtes API en cours (0 = le réseau est au repos). */
export function apiInFlight() {
  return inFlight;
}

const settle = () => { inFlight = Math.max(0, inFlight - 1); };
api.interceptors.request.use(
  (config) => { inFlight += 1; return config; },
  (error) => { settle(); return Promise.reject(error); },
);
api.interceptors.response.use(
  (response) => { settle(); return response; },
  (error) => { settle(); return Promise.reject(error); },
);

/* VERSION DE PLAN (2026-09-27) : la version lue et écrite par le client actif
   voyage dans l'en-tête `X-Plan-Version` de chaque requête qui nomme ce
   client. Sans en-tête, le serveur lit la version COURANTE. App la pose à
   chaque changement de client ou de version ; elle ne se pose que pour les
   requêtes du client concerné, pour qu'un changement de client ne porte jamais
   la version du précédent. */
const PLAN_VERSION_HEADER = 'X-Plan-Version';
let planVersion = { tenantId: null, versionId: null };

export function setPlanVersion(tenantId, versionId) {
  planVersion = { tenantId: tenantId || null, versionId: versionId || null };
}

export function planVersionStorageKey(tenantId) {
  return `palestre_plan_version_${tenantId}`;
}

api.interceptors.request.use((config) => {
  const { tenantId, versionId } = planVersion;
  if (tenantId && versionId && String(config.url || '').includes(tenantId)) {
    config.headers = config.headers || {};
    config.headers[PLAN_VERSION_HEADER] = versionId;
  }
  return config;
});

const ACCESS_KEY = 'palestre_access_token';
const REFRESH_KEY = 'palestre_refresh_token';

// Le JWT d'accès reste uniquement en mémoire : une injection JavaScript ne
// peut plus le récupérer après coup dans un stockage persistant. Supprime aussi
// les JWT laissés par les versions précédentes lors du premier chargement.
let accessToken = null;
localStorage.removeItem(ACCESS_KEY);

function getAccessToken() {
  return accessToken;
}

function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

function setTokens(access, refresh) {
  if (access) accessToken = access;
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

function clearTokens() {
  accessToken = null;
  // Nettoyage défensif pour les sessions créées avant cette migration.
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise = null;

async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) {
    clearTokens();
    throw new Error('no refresh token');
  }
  // Appel sans intercepteur de retry (instance nue) pour éviter une boucle.
  const { data } = await axios.post('/api/auth/refresh', { refresh_token: refresh });
  const payload = data?.data;
  if (!payload?.access_token || !payload?.refresh_token) {
    clearTokens();
    throw new Error('refresh failed');
  }
  setTokens(payload.access_token, payload.refresh_token);
  return payload.access_token;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || '';
    const isAuthRoute =
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout');

    // 403 sur une ÉCRITURE : le refus doit être VU. Le message discret en bas
    // d'écran passait inaperçu — l'utilisateur croyait à une panne et
    // recommençait. On émet un événement ; App affiche une barre en haut.
    if (status === 403) {
      const method = String(original?.method || 'get').toUpperCase();
      if (method !== 'GET' && method !== 'HEAD') {
        const detail = error.response?.data?.detail;
        window.dispatchEvent(new CustomEvent('palestre:forbidden', {
          detail: typeof detail === 'string' ? detail : null,
        }));
      }
    }

    /* VERSION DE PLAN DISPARUE (2026-09-27) : la version retenue par ce
       navigateur a été supprimée ailleurs, et le serveur refuse chaque requête
       qui la nomme (404). On le dit à App, qui revient à la version courante et
       relit la liste ; rien ne se devine côté serveur. */
    if (status === 404) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail.startsWith('Version de plan introuvable')) {
        window.dispatchEvent(new CustomEvent('palestre:plan-version-gone'));
      }
    }

    if (status !== 401 || !original || original._retry || isAuthRoute) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const access = await refreshPromise;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${access}`;
      return api(original);
    } catch {
      clearTokens();
      window.dispatchEvent(new CustomEvent('palestre:auth-expired'));
      return Promise.reject(error);
    }
  }
);

// Normalise un détail d'erreur en chaîne affichable. FastAPI renvoie sur 422 un
// `detail` = tableau d'objets Pydantic {type, loc, msg, input} : les rendre tels
// quels comme enfant React casse toute la page (« Objects are not valid as a
// React child »). On garantit ici que `error` est toujours une string (ou null).
function _toMessage(x) {
  if (x == null) return null;
  if (typeof x === 'string') return x;
  if (Array.isArray(x)) return x.map(_toMessage).filter(Boolean).join(' · ') || null;
  if (typeof x === 'object') {
    if (typeof x.msg === 'string') {
      const loc = Array.isArray(x.loc) ? x.loc.filter(p => p !== 'body').join('.') : '';
      return loc ? `${loc} : ${x.msg}` : x.msg;
    }
    try { return JSON.stringify(x); } catch { return String(x); }
  }
  return String(x);
}

// Le backend renvoie {data, error} même sur 4xx/5xx, mais axios rejette sur
// tout non-2xx (et sur les erreurs réseau) : unwrap rattrape et garantit le
// contrat {data, error, status} à l'appelant — jamais de promesse rejetée.
export const unwrap = (promise) =>
  promise
    .then(r => r.data)
    .catch(e => {
      const body = e.response?.data;
      // Contrat {data, error} pour la plupart des routes ; mais certaines lèvent
      // une HTTPException FastAPI (corps {detail}). On remonte l'un ou l'autre,
      // sinon le message réel est masqué par le générique « status code 400 ».
      const error =
        _toMessage((body && typeof body === 'object' && (body.error || body.detail)) || e.message);
      const status = e.response?.status ?? null;
      // Remonte un toast global (App.js écoute) — sauf :
      // 404 = pas encore de données ; 401 = gate auth ; 403 = hors périmètre
      // de rôle (dégradation attendue quand une page appelle une API d'un
      // autre module en optionnel).
      if (status !== 404 && status !== 401 && status !== 403) {
        // La SOURCE voyage avec le message : le bouton « Copier » du message
        // d'erreur la recopie (méthode, adresse, statut).
        const cfg = e.config || {};
        const url = /^https?:/.test(cfg.url || '') ? cfg.url : `${cfg.baseURL || ''}${cfg.url || ''}`;
        const request = cfg.url ? `${(cfg.method || 'get').toUpperCase()} ${url}` : null;
        /* UN REFUS N'EST PAS UNE PANNE (2026-10-09) : une règle qui dit non
           (version verrouillée 409, mois clos et autres refus motivés 400)
           s'affiche en INFORMATION ; le rouge reste aux vraies erreurs. */
        const kind = status === 400 || status === 409 ? 'info' : 'error';
        window.dispatchEvent(new CustomEvent('palestre:api-error', {
          detail: { error, status, kind, source: { request, status } },
        }));
      }
      return { data: null, error, status };
    });

/**
 * Télécharge un fichier binaire (Excel, PowerPoint, JSON) et déclenche la
 * sauvegarde navigateur. Passe par `api` — donc jeton d'accès porté ET
 * rafraîchissement automatique : un `fetch()` nu ne serait pas authentifié.
 */
export async function downloadFile(path, filename) {
  let res;
  try {
    res = await api.get(path, { responseType: 'blob' });
  } catch (e) {
    // Sur erreur, le corps est un Blob : le lire pour un message exploitable.
    let detail = e.message;
    const body = e.response?.data;
    if (body instanceof Blob) {
      try { detail = _toMessage(JSON.parse(await body.text())?.detail) || detail; }
      catch { /* corps non JSON : on garde le message axios */ }
    }
    throw new Error(detail);
  }
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export { getAccessToken, getRefreshToken, setTokens, clearTokens };
export default api;
